import { GenericBackendResponse } from '../genericBackendResponse';

/**
 * Un plan del usuario tal y como lo sirve `GET /users/plans`.
 *
 * `id` es lo único que distingue dos planes del MISMO tipo (un usuario puede
 * tener dos INDIVIDUAL): cualquier lectura o escritura de un plan concreto
 * tiene que viajar por `id`/`planCatalogId`, nunca por `type`.
 */
export interface UserPlanI {
	id: string;
	type: 'EMPLOYMENT' | 'INDIVIDUAL' | 'FREELANCE' | null;
	/** `active` (sincroniza y aporta) · `suspended` (sincroniza, no aporta) · `archived` (ninguna). */
	status: 'active' | 'suspended' | 'archived';
	planCatalogId: string | null;
	/**
	 * Slug del catálogo (`indexa-mas-rentabilidad-ppi`), para pedir su rentabilidad a
	 * `/plan-returns` (chirigota#598). `null` sin catálogo; ausente con un comparsa anterior a
	 * comparsa#832.
	 */
	planCatalogSlug?: string | null;
	/** Productos Caser de ESTE plan. Vacío si el plan no tiene catálogo o el catálogo no declara productos. */
	productCodes: number[];
	companyId: string | null;
	/**
	 * Nombre presentable del producto ("Indexa Más Rentabilidad Acc."), el que se
	 * pinta en la card. `null` si el plan no tiene catálogo.
	 */
	planDisplayName: string | null;
	/** Gestora del plan ("Indexa Capital", "Caser"). */
	managerName: string | null;
	/**
	 * Empresa del plan de empleo. No es lo mismo que `managerName` y la card los
	 * usa distinto: un plan de EMPLEO se subtitula con su EMPRESA, uno individual
	 * con su GESTORA. `null` en individuales y autónomos.
	 */
	companyName: string | null;
	/**
	 * 🔴 Derechos consolidados de ESTE plan, en CÉNTIMOS — hay que dividir para
	 * pintar euros. Y `null` NO es cero, es "todavía no se sabe": o el dinero no
	 * se puede atribuir a un plan concreto, o la foto diaria aún no ha pasado por
	 * este plan (alta reciente, o cron nocturno caído). Un `0` sólo llega cuando
	 * el dato existe y dice cero. Pintar `null` como 0 € le diría a alguien que su
	 * plan está vacío, así que hay que tratarlo aparte (un guion, un "—", lo que
	 * decida diseño, pero nunca un importe).
	 */
	vestedRightsCents: number | null;
	/**
	 * Derechos consolidados a 1 de enero del año en curso, en CÉNTIMOS (la última foto de
	 * diciembre): con ellos se calcula la rentabilidad del año de la card. `0` si el plan se abrió
	 * este año. 🔴 `null` NO es cero: no se sabe. Ausente con un comparsa anterior.
	 */
	vestedRightsAtYearStartCents?: number | null;
	/**
	 * Día (`AAAA-MM-DD`) de la foto de la que sale `vestedRightsAtYearStartCents`. Suele ser el
	 * 31/12; si falló la sync de fin de año, anterior. `null` sin foto de partida (plan abierto este
	 * año, o saldo desconocido). Ausente con un comparsa anterior.
	 */
	vestedRightsAtYearStartDate?: string | null;
	/** Alta del plan: antigüedad, y fecha desde la que hay serie que pintar. */
	createdAt: string;
	/**
	 * Última aportación recibida por este plan. `null` sólo si no ha recibido
	 * ninguna — es lo que separa "activo aportando" de "activo sin aportación
	 * programada".
	 */
	lastContributionDate: string | null;
}

/**
 * `GET /users/plans` — los planes del usuario del token, desde BBDD (no llama a
 * Caser en vivo). Fuente única de "qué planes tiene esta persona": se pide UNA
 * vez al montar el Dashboard, en vez de que cada vista derive sus
 * `productCodes` a mano desde `getAdhesionDetails`.
 *
 * `data` es `[]` cuando el usuario no tiene planes — nunca un 404.
 */
const getPlans = async (token: string): Promise<GenericBackendResponse<UserPlanI[]>> => {
	const response = await fetch(`${process.env.REACT_APP_BASE_API}/users/plans`, {
		method: 'GET',
		headers: {
			'Content-Type': 'application/json',
			Authorization: `Bearer ${token}`
		}
	});

	// Un 404 o un 500 no son una lista de planes: sin esto, el cuerpo de error
	// saldría de aquí tipado como éxito y el primer `data.map(...)` reventaría.
	// El 404 está documentado (token de un usuario que ya no existe) y el 404 de
	// "endpoint inexistente" ocurre si este front se despliega antes que el
	// backend. Quien llame decide qué hacer; hoy el Dashboard lo ignora a
	// propósito porque aún no consume la respuesta.
	if (!response.ok) {
		throw new Error(`GET /users/plans respondió ${response.status}`);
	}

	return response.json();
};

export default getPlans;
